/* eslint-disable react-hooks/exhaustive-deps */
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { IPullDownItem } from 'Types/Common';
import { useQueryParams, StringParam, NumberParam } from 'use-query-params';
import {
  handleGetIds,
  handleSearchArtist,
  handleSearchCompanySample,
  handleSearchTitleSample,
  handleSearchVariationSample,
  PageQuery,
  updateQuery,
} from 'Utils/Search';
import useMessage from './useMessage';

export interface SearchResponse {
  options: IPullDownItem[];
  totalPages: number;
}

interface ValidQueryCommon {
  [key: string]: string | number | undefined;
}
interface Props {
  validQueryObj?: {
    validCompany?: ValidQueryCommon;
    validArtist?: ValidQueryCommon;
    validTitle?: ValidQueryCommon;
    validVariation?: ValidQueryCommon;
  };
  fetchList: string[];
}

export default function useSuggestionSample({ validQueryObj, fetchList }: Props): {
  companyOptions: SearchResponse;
  artistOptions: SearchResponse;
  titleOptions: SearchResponse;
  variationOptions: SearchResponse;
  pageObj: PageQuery;
  loadingArtist: boolean;
  loadingTitle: boolean;
  loadingVariation: boolean;
  loadingCompany: boolean;
  handleLoadMore: (pageKey: string) => void;
  handleFocusInput: (queryKey: string, pageKey: string) => void;
  handleEnterPrimary: (type: string) => void;
  handleClickItemPrimary: (id: string, type: string) => void;
} {
  //* some query clear with search key clear
  const currentValidQuery = {
    validCompany: {
      ...validQueryObj?.validCompany,
      companyIds: undefined,
      queryCompany: undefined,
      companyId: undefined,
    },
    validArtist: {
      ...validQueryObj?.validArtist,
      artistIds: undefined,
      queryArtist: undefined,
      artistId: undefined,
    },
    validTitle: {
      ...validQueryObj?.validTitle,
      queryTitle: undefined,
      titleIds: undefined,
      titleId: undefined,
    },
    validVariation: {
      ...validQueryObj?.validVariation,
      queryVariation: undefined,
      variationIds: undefined,
      variationId: undefined,
    },
  };

  const [query, setQuery] = useQueryParams({
    queryArtist: StringParam,
    queryTitle: StringParam,
    artistIds: StringParam,
    titleIds: StringParam,
    variationIds: StringParam,
    queryVariation: StringParam,
    artistId: StringParam,
    titleId: StringParam,
    variationId: StringParam,
    companyId: StringParam,
    companyIds: StringParam,
    queryCompany: StringParam,
    page: NumberParam,
  });

  const { openMessageError } = useMessage();

  const [queryObj, setQueryObj] = useState({
    queryArtist: query.queryArtist,
    queryTitle: query.queryTitle,
    queryVariation: query.queryVariation,
    queryCompany: query.queryCompany,
  });

  const [pageObj, setPageObj] = useState<PageQuery>({
    pageArtist: 1,
    pageTitle: 1,
    pageVariation: 1,
    pageCompany: 1,
  });

  const [artists, setArtists] = useState<SearchResponse>({ options: [], totalPages: 0 });
  const [titles, setTitles] = useState<SearchResponse>({ options: [], totalPages: 0 });
  const [variations, setVariations] = useState<SearchResponse>({ options: [], totalPages: 0 });
  const [companies, setCompanies] = useState<SearchResponse>({ options: [], totalPages: 0 });

  const [artistTmp, setArtistTmp] = useState<IPullDownItem[]>([]);
  const [titleTmp, setTitleTmp] = useState<IPullDownItem[]>([]);
  const [variationTmp, setVariationTmp] = useState<IPullDownItem[]>([]);
  const [companyTmp, setCompanyTmp] = useState<IPullDownItem[]>([]);

  const [loadingArtist, setLoadingArtist] = useState<boolean>(false);
  const [loadingTitle, setLoadingTitle] = useState<boolean>(false);
  const [loadingVariation, setLoadingVariation] = useState<boolean>(false);
  const [loadingCompany, setLoadingCompany] = useState<boolean>(false);

  const handleFetchCompanies = async () => {
    try {
      setLoadingCompany(true);
      const {
        data: { data },
      } = await handleSearchCompanySample({
        page: pageObj.pageCompany,
        queryCompany: queryObj.queryCompany || '',
      });

      const options = data?.items?.map((company: IPullDownItem) => ({
        label: company.companyName,
        value: company.companyId,
      }));
      setCompanies({
        options: pageObj.pageCompany === 1 ? options : [...companies.options, ...options],
        totalPages: data?.meta?.totalPages,
      });

      if (queryObj.queryCompany) {
        setQuery({ companyIds: companies.options[0]?.value as string, page: 1 });
      }
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleFetchArtists = async () => {
    try {
      setLoadingArtist(true);
      const {
        data: { data },
      } = await handleSearchArtist({
        queryArtist: queryObj.queryArtist || '',
        page: pageObj.pageArtist,
        companyId: query.companyId || query.companyIds || '',
      });

      const options = data?.items?.map((artist: IPullDownItem) => ({
        label: artist.localName,
        value: artist.id,
      }));
      setArtists({
        options: pageObj.pageArtist === 1 ? options : [...artists.options, ...options],
        totalPages: data?.meta?.totalPages,
      });
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoadingArtist(false);
    }
  };

  const handleFetchTitles = async () => {
    try {
      setLoadingTitle(true);
      const {
        data: { data },
      } = await handleSearchTitleSample({
        queryTitle: queryObj.queryTitle || '',
        artistId: query.artistId || query.artistIds || '',
        page: pageObj.pageTitle,
        companyId: query.companyId || query.companyIds || '',
      });

      const options = data?.items?.map((title: IPullDownItem) => ({
        label: title.titleName,
        value: title.titleId,
      }));

      setTitles({
        options: pageObj.pageTitle === 1 ? options : [...titles.options, ...options],
        totalPages: data?.meta?.totalPages,
      });
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoadingTitle(false);
    }
  };

  const handleFetchVariations = async () => {
    try {
      setLoadingVariation(true);
      const {
        data: { data },
      } = await handleSearchVariationSample({
        queryVariation: queryObj.queryVariation || '',
        artistId: query.artistId || query.artistIds || '',
        titleId: query.titleId || query.titleIds || '',
        page: pageObj.pageVariation,
        companyId: query.companyId || query.companyIds || '',
      });

      const options = data?.items?.map((item: IPullDownItem) => ({
        label: item.name,
        value: item.id,
      }));

      setVariations({
        options: pageObj.pageVariation === 1 ? options : [...variations.options, ...options],
        totalPages: data?.meta?.totalPages,
      });
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoadingVariation(false);
    }
  };

  const debouceRequest = useCallback((value) => updateQuery(value, setQueryObj), []);

  //* debouce typing text 300ms
  useEffect(() => {
    debouceRequest({
      queryArtist: query.queryArtist,
      queryTitle: query.queryTitle,
      queryVariation: query.queryVariation,
      queryCompany: query.queryCompany,
    });
  }, [
    query.queryArtist,
    query.queryTitle,
    query.queryVariation,
    query.queryTitle,
    query.queryCompany,
  ]);

  //* only call api when match key search
  useEffect(() => {
    if (fetchList.includes('company')) handleFetchCompanies();
  }, [queryObj, pageObj.pageCompany]);

  useEffect(() => {
    if (fetchList.includes('artist')) handleFetchArtists();
  }, [queryObj, pageObj.pageArtist]);

  useEffect(() => {
    if (fetchList.includes('title')) handleFetchTitles();
  }, [queryObj, pageObj.pageTitle]);

  useEffect(() => {
    if (fetchList.includes('variation')) handleFetchVariations();
  }, [queryObj, pageObj.pageVariation]);

  //* clear some query if text search clear
  useEffect(() => {
    let validQuery = { ...query } as any;

    if (!query.queryCompany && !isEmpty(currentValidQuery.validCompany)) {
      validQuery = currentValidQuery.validCompany;
      setQuery(validQuery);
    }

    setPageObj({ pageCompany: 1, pageArtist: 1, pageTitle: 1, pageVariation: 1 });
  }, [query.queryCompany]);

  useEffect(() => {
    let validQuery = { ...query } as any;

    if (!query.queryArtist && !isEmpty(currentValidQuery.validArtist)) {
      validQuery = currentValidQuery.validArtist;
      setQuery(validQuery);
    }

    setPageObj({ pageArtist: 1, pageTitle: 1, pageVariation: 1 });
  }, [query.queryArtist]);

  useEffect(() => {
    let validQuery = { ...query } as any;
    if (!query.queryTitle && !isEmpty(currentValidQuery.validTitle)) {
      validQuery = currentValidQuery.validTitle;
      setQuery(validQuery);
    }

    setPageObj({ ...pageObj, pageTitle: 1, pageVariation: 1 });
  }, [query.queryTitle]);

  useEffect(() => {
    let validQuery = { ...query } as any;
    if (!query.queryVariation && !isEmpty(currentValidQuery.validVariation)) {
      validQuery = currentValidQuery.validVariation;
      setQuery(validQuery);
    }

    setPageObj({ ...pageObj, pageVariation: 1 });
  }, [query.queryVariation]);

  const handleLoadMore = (pageKey: string) => {
    if (pageKey === 'pageCompany' && pageObj.pageCompany < companies.totalPages)
      setPageObj({ ...pageObj, pageCompany: (pageObj.pageCompany += 1) });

    if (pageKey === 'pageArtist' && pageObj.pageArtist < artists.totalPages)
      setPageObj({ ...pageObj, pageArtist: (pageObj.pageArtist += 1) });

    if (pageKey === 'pageTitle' && pageObj.pageTitle < titles.totalPages)
      setPageObj({ ...pageObj, pageTitle: (pageObj.pageTitle += 1) });

    if (pageKey === 'pageVariation' && pageObj.pageVariation < variations.totalPages)
      setPageObj({ ...pageObj, pageVariation: (pageObj.pageVariation += 1) });
  };

  useEffect(() => {
    if (queryObj.queryCompany && query.queryCompany) {
      setCompanyTmp(companies.options);
    }

    if (!query.queryCompany) {
      setCompanyTmp([]);
    }
  }, [companies.options, query.queryCompany, queryObj.queryCompany]);

  useEffect(() => {
    if (queryObj.queryArtist && query.queryArtist) {
      setArtistTmp(artists.options);
    }

    if (!query.queryArtist) {
      setArtistTmp([]);
    }
  }, [artists.options, query.queryArtist, queryObj.queryArtist]);

  useEffect(() => {
    if (queryObj.queryTitle && query.queryTitle) {
      setTitleTmp(titles.options);
    }

    if (!query.queryTitle) {
      setTitleTmp([]);
    }
  }, [query.queryTitle, queryObj.queryTitle, titles.options]);

  useEffect(() => {
    if (queryObj.queryVariation && query.queryVariation) {
      setVariationTmp(variations.options);
    }

    if (!query.queryVariation) {
      setVariationTmp([]);
    }
  }, [query.queryTitle, query.queryVariation, queryObj.queryVariation, variations.options]);

  //* when open menu, reset list menu all data
  const handleFocusInput = useCallback(
    (queryKey: string, pageKey: string) => {
      if (queryKey === 'queryCompany') {
        setCompanies({ options: [], totalPages: 0 });
      }
      if (queryKey === 'queryArtist') {
        setArtists({ options: [], totalPages: 0 });
      }

      if (queryKey === 'queryTitle') {
        setTitles({ options: [], totalPages: 0 });
      }

      if (queryKey === 'queryVariation') {
        setVariations({ options: [], totalPages: 0 });
      }

      setQueryObj({ ...queryObj, [queryKey]: '' });
      setPageObj({ ...pageObj, [pageKey]: 1 });
    },
    [pageObj, queryObj],
  );

  const handleEnterPrimary = useCallback(
    (type: string) => {
      const res = [artistTmp, titleTmp, variationTmp, companyTmp];
      const ids = res.map((item: any) => handleGetIds(item));
      if (type === 'queryArtist') {
        const artistIds = query[type] ? (ids[0] ? ids[0] : '') : '';
        setQuery({
          artistIds: artistIds as string,
          page: 1,
        });
      }

      if (type === 'queryTitle') {
        const titleIds = query[type] ? (ids[1] ? ids[1] : '') : '';
        setQuery({
          titleIds: titleIds as string,
          page: 1,
        });
      }

      if (type === 'queryVariation') {
        const variationIds = query[type] ? (ids[2] ? ids[2] : '') : '';
        setQuery({
          variationIds: variationIds as string,
          page: 1,
        });
      }

      if (type === 'queryCompany') {
        const companyIds = query[type] ? (ids[3] ? ids[3] : '') : '';
        setQuery({
          companyIds: companyIds as string,
          page: 1,
        });
      }
    },
    [artistTmp, query, setQuery, titleTmp, variationTmp, companyTmp],
  );

  const handleClickItemPrimary = useCallback(
    (id: string, type: string) => {
      if (type === 'queryArtist') {
        setQuery({ artistId: id, artistIds: id, page: 1 });
      }

      if (type === 'queryTitle') {
        setQuery({ titleId: id, page: 1, titleIds: id });
      }

      if (type === 'queryVariation') {
        setQuery({ variationId: id, page: 1, variationIds: id });
      }

      if (type === 'queryCompany') {
        setQuery({ companyId: id, page: 1, companyIds: id });
      }
    },
    [setQuery],
  );

  return {
    companyOptions: companies,
    artistOptions: artists,
    titleOptions: titles,
    variationOptions: variations,
    pageObj,
    loadingArtist,
    loadingTitle,
    loadingVariation,
    loadingCompany,
    handleLoadMore,
    handleFocusInput,
    handleEnterPrimary,
    handleClickItemPrimary,
  };
}
